import { useCallback, useEffect, useState } from 'react';

import { getSetup, leaveCompany, listNotifications, logout, type Me } from '../api.js';
import { roleLabel } from '../labels.js';
import { AppLink } from '../link.js';
import { projectRouteFrom, usePath } from '../router.js';
import { THEME_CHOICES, THEME_LABELS, applyTheme, readTheme, type ThemeChoice } from '../theme.js';
import { sectionFrom } from './project-sections.js';
import { AuditTrail } from './AuditTrail.js';
import { Portfolio } from './Portfolio.js';
import { Company } from './Company.js';
import { MyWork } from './MyWork.js';
import { Notifications } from './Notifications.js';
import { People } from './People.js';
import { ProjectDetail } from './ProjectDetail.js';
import { Projects } from './Projects.js';
import { ON_SITE, SiteHome } from './SiteHome.js';
import { SupportBanner } from './SupportBanner.js';
import { BrandMark, PRODUCT_LINE } from '../brand.js';

interface Props {
  readonly me: Me;
  readonly onSignedOut: () => void;
}

/**
 * The navigation icons, drawn here rather than fetched.
 *
 * Inline SVG for two reasons: an icon font or a CDN sprite is a network
 * request that can fail and leave a row of empty boxes, and this app is
 * expected to run on a site office connection. They are 20px on a 24 grid,
 * stroked not filled, so they sit at the same weight as the label beside them.
 *
 * Every one is `aria-hidden` at the call site and always paired with its word.
 */
const svg = (path: JSX.Element): JSX.Element => (
  <svg
    viewBox="0 0 24 24"
    width="20"
    height="20"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    focusable="false"
  >
    {path}
  </svg>
);

const ICONS = {
  portfolio: svg(
    <>
      <rect x="3" y="3" width="7" height="9" rx="1" />
      <rect x="14" y="3" width="7" height="5" rx="1" />
      <rect x="14" y="12" width="7" height="9" rx="1" />
      <rect x="3" y="16" width="7" height="5" rx="1" />
    </>,
  ),
  myWork: svg(
    <>
      <path d="M9 11l2 2 4-4" />
      <rect x="3" y="4" width="18" height="17" rx="2" />
      <path d="M8 2v4M16 2v4" />
    </>,
  ),
  projects: svg(
    <>
      <path d="M3 21V8l9-5 9 5v13" />
      <path d="M9 21v-6h6v6" />
    </>,
  ),
  alerts: svg(
    <>
      <path d="M18 8a6 6 0 10-12 0c0 7-3 8-3 8h18s-3-1-3-8" />
      <path d="M13.7 21a2 2 0 01-3.4 0" />
    </>,
  ),
  people: svg(
    <>
      <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 00-3-3.87" />
    </>,
  ),
  company: svg(
    <>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M8 21V9h8v12" />
      <path d="M11 12h2M11 15h2M11 18h2" />
    </>,
  ),
  audit: svg(
    <>
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <path d="M14 2v6h6" />
      <path d="M8 13h8M8 17h5" />
    </>,
  ),
  collapse: svg(
    <>
      <path d="M15 6l-6 6 6 6" />
    </>,
  ),
  expand: svg(
    <>
      <path d="M9 6l6 6-6 6" />
    </>,
  ),
} as const;

/**
 * Is the rail collapsed? Remembered between visits.
 *
 * A preference about the furniture, not about the data, so `localStorage` is
 * the right place for it — it never leaves the browser and it does not belong
 * in anybody's company record. Wrapped because a browser with storage disabled
 * throws on access, and losing the rail's width is not worth losing the app.
 */
const RAIL_KEY = 'fitout:rail-collapsed';

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(RAIL_KEY) === '1';
  } catch {
    return false;
  }
}

function writeCollapsed(value: boolean): void {
  try {
    window.localStorage.setItem(RAIL_KEY, value ? '1' : '0');
  } catch {
    /* a preference is not worth an error */
  }
}

export function Home({ me, onSignedOut }: Props): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [unread, setUnread] = useState(0);
  const [setupLeft, setSetupLeft] = useState(0);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [menuOpen, setMenuOpen] = useState(false);
  /*
   * Seeded from what the blocking script in `index.html` already applied, so
   * the menu opens showing the truth rather than a default that disagrees with
   * the page the reader is looking at.
   */
  const [theme, setTheme] = useState<ThemeChoice>(readTheme);
  const path = usePath();
  const projectRoute = projectRouteFrom(path);
  const onHome = path === '/' || path.startsWith('/home');
  const onPeople = path.startsWith('/people');
  const onCompany = path.startsWith('/company');
  const onAudit = path.startsWith('/audit');
  const onMyWork = path.startsWith('/my-work');
  const onAlerts = path.startsWith('/notifications');

  /**
   * The unread count, fetched once on load.
   *
   * Not polled. A badge that updates every thirty seconds is a badge that
   * costs a request every thirty seconds for every person signed in, and this
   * runs on one workstation. It refreshes when the notifications screen is
   * opened or an item is read, which is when it actually matters.
   */
  const refreshUnread = useCallback(async () => {
    try {
      setUnread((await listNotifications()).unread);
    } catch {
      /* a badge is not worth an error message */
    }
  }, []);

  useEffect(() => {
    void refreshUnread();
  }, [refreshUnread]);

  /**
   * How much setting up is left, for the badge on the Company tab.
   *
   * Re-read whenever the page changes, because the things it counts — a team
   * member, a first project — are created on the other tabs. A count that only
   * loaded once would keep nagging about a project the person just made.
   */
  useEffect(() => {
    if (!me.can.view_company) return;
    void getSetup()
      .then((setup) => setSetupLeft(setup.dismissed ? 0 : setup.remaining))
      .catch(() => setSetupLeft(0));
  }, [me.can.view_company, path]);

  /**
   * The vendor, in this company on a support visit — almost never true.
   *
   * It changes three things and no more: the name in the corner, the line in
   * the account menu, and what the button at the bottom of it does. Everything
   * else on the screen is already correct, because `me.can` is computed from
   * the visit's own view-only permissions and the buttons a visitor may not
   * press are simply not drawn.
   */
  const visiting = me.support?.you_are_visiting === true;

  async function onSignOut(): Promise<void> {
    setBusy(true);
    try {
      /*
       * A VISITOR LEAVES; THEY DO NOT SIGN OUT.
       *
       * `logout()` ends a session in `session`, and a visitor has none — an
       * operator is not one of this company's people. Calling it would end
       * nothing, leave the visit open, and drop them back on a sign-in screen
       * for a company they have no account in. Ending the visit is the act that
       * matches the button, and it is the one written into both trails.
       */
      if (visiting) {
        await leaveCompany();
        window.location.assign('/operator');
        return;
      }
      await logout();
    } finally {
      // Sign the user out locally even if the request failed — leaving them
      // looking signed in when they asked to leave is the worse outcome.
      if (!visiting) onSignedOut();
    }
  }

  /**
   * A navigation item. A real link, so it can be opened in a new tab or
   * bookmarked.
   *
   * `badgeMeans` is not decoration: the number alone is meaningless to a screen
   * reader, and "3" on Alerts and "3" on Company mean completely different
   * things.
   *
   * The icon is `aria-hidden` and always paired with the word. An icon-only
   * rail looks tidier and makes everybody guess; on a product with seven
   * destinations there is room for both.
   */
  const tab = (
    href: string,
    label: string,
    current: boolean,
    icon: JSX.Element,
    badge?: number,
    badgeMeans = 'unread',
  ): JSX.Element => (
    <AppLink
      to={href}
      className={current ? 'nav-tab current' : 'nav-tab'}
      aria-current={current ? 'page' : undefined}
    >
      <span className="nav-icon" aria-hidden="true">
        {icon}
      </span>
      <span className="nav-label">{label}</span>
      {badge !== undefined && badge > 0 && (
        <span className="nav-badge">
          {badge > 99 ? '99+' : badge}
          <span className="visually-hidden"> {badgeMeans}</span>
        </span>
      )}
    </AppLink>
  );

  return (
    <>
      {/*
        ABOVE THE SHELL, NOT INSIDE IT.

        The shell is a two-column grid on a wide screen — rail, then content —
        and anything placed inside it becomes a column. A sibling before it is a
        full-width strip across the top of the page with no layout to negotiate,
        which is exactly what a warning nobody may miss should be.

        Almost always absent. `SupportBanner` explains why it exists and why
        both sides of a visit see one.
      */}
      {me.support && <SupportBanner visit={me.support} />}

      <div className={collapsed ? 'shell is-collapsed' : 'shell'}>
      {/*
        A rail on a wide screen, a scrolling strip along the top on a phone —
        one element, two shapes, decided in CSS. It replaced a top bar whose
        seven links pushed the page 1,400px wide at 320px.
      */}
      <header className="app-bar">
        <div className="app-brand">
          <span className="app-mark" aria-hidden="true">
            <BrandMark size={24} />
          </span>
          <span className="app-brand-name">{me.company_name ?? PRODUCT_LINE}</span>

          {/*
            Collapsing narrows the rail to its icons and keeps the labels as
            tooltips and accessible names — it never becomes a row of unlabelled
            glyphs. Desktop only: on a phone the rail is already a strip.
          */}
          <button
            type="button"
            className="rail-toggle"
            aria-pressed={collapsed}
            title={collapsed ? 'Widen the sidebar' : 'Narrow the sidebar'}
            onClick={() => {
              const next = !collapsed;
              setCollapsed(next);
              writeCollapsed(next);
            }}
          >
            <span className="visually-hidden">
              {collapsed ? 'Widen the sidebar' : 'Narrow the sidebar'}
            </span>
            <span aria-hidden="true">{collapsed ? ICONS.expand : ICONS.collapse}</span>
          </button>
        </div>

        <nav className="app-nav" aria-label="Sections">
          {/* First, because it is where a director starts the day (W1). */}
          {tab('/home', 'Portfolio', onHome, ICONS.portfolio)}
          {tab('/my-work', 'My work', onMyWork, ICONS.myWork)}
          {tab(
            '/projects',
            'Projects',
            !onHome && !onPeople && !onCompany && !onAudit && !onMyWork && !onAlerts,
            ICONS.projects,
          )}
          {tab('/notifications', 'Alerts', onAlerts, ICONS.alerts, unread)}
          {/* Shown only to people who may actually open it — the server decides,
              and the route refuses independently if this is ever wrong. */}
          {me.can.view_users && tab('/people', 'People', onPeople, ICONS.people)}
          {me.can.view_company &&
            tab(
              '/company',
              'Company',
              onCompany,
              ICONS.company,
              setupLeft,
              'things left to set up',
            )}
          {me.can.view_audit && tab('/audit', 'Audit', onAudit, ICONS.audit)}
        </nav>

        {/*
          The account menu. It exists to say WHO YOU ARE HERE before it offers
          to sign you out: the same person is a Director in one company and a
          Consultant in another, and every screen in this product is a different
          screen depending on which. The role and the financial-access flag are
          the two facts that explain what somebody can and cannot see, so they
          are named rather than left to be inferred from missing buttons.
        */}
        {/*
          THEME, IN THE RAIL AND ABOVE THE NAME — not buried in the account menu.
          The user's words: it should "be from outside, above the name".

          A three-way segmented control rather than a light/dark switch, because
          there are genuinely three answers and "follow the machine" is the one
          most people want. A two-state switch forces everybody to pick a side
          and then stops tracking their system at sunset.

          "Auto" is the visible word because the control is 208px wide in the
          rail; the full "Use system setting" is the accessible name, so a screen
          reader is told what it actually does.
        */}
        <div
          className="theme-switch"
          role="radiogroup"
          aria-label="Theme"
          onKeyDown={(event) => {
            if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
            event.preventDefault();
            const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
            const at = THEME_CHOICES.indexOf(theme);
            const next =
              THEME_CHOICES[(at + step + THEME_CHOICES.length) % THEME_CHOICES.length]!;
            setTheme(next);
            applyTheme(next);
          }}
        >
          {THEME_CHOICES.map((choice) => (
            <button
              key={choice}
              type="button"
              role="radio"
              aria-checked={theme === choice}
              aria-label={THEME_LABELS[choice]}
              /* Roving tabindex: one tab stop for the group, arrows inside it. */
              tabIndex={theme === choice ? 0 : -1}
              className="theme-switch-option"
              onClick={() => {
                setTheme(choice);
                applyTheme(choice);
              }}
            >
              {choice === 'system' ? 'Auto' : THEME_LABELS[choice]}
            </button>
          ))}
        </div>

        <div className="app-user">
          <button
            type="button"
            className="user-button"
            aria-expanded={menuOpen}
            aria-haspopup="true"
            onClick={() => setMenuOpen((was) => !was)}
          >
            {/*
              "Account" leads the accessible name, so the control is findable by
              what it IS rather than by whose role happens to be in it — the
              visible text is a role and a company, which change per person and
              per tenant. A hidden PREFIX rather than an `aria-label`, because
              an aria-label would replace the visible words instead of extending
              them (WCAG 2.2 §2.5.3, Label in Name).
            */}
            <span className="visually-hidden">Account: </span>
            {/*
              A VISITOR IS NOT A COLLEAGUE, so this corner must not say they are.

              `/v1/me` returns a null role during a support visit on purpose: the
              visit acts with Company Admin's grants filtered down to `view`, and
              printing "Company Admin at Al Rakha Interiors" above the vendor's
              own name would be the same falsehood the audit trail refuses to
              write. Without this branch the fallback reads "Unknown role", which
              is not a lie but explains nothing.
            */}
            <span className="user-avatar" aria-hidden="true">
              {visiting ? '·' : (me.company_role ?? '?').slice(0, 1).toUpperCase()}
            </span>
            <span className="signed-in-as">
              <span className="signed-in-role">
                {visiting
                  ? 'Support visit'
                  : me.company_role
                    ? roleLabel(me.company_role)
                    : 'Unknown role'}
              </span>
              <span className="signed-in-company">{me.company_name ?? PRODUCT_LINE}</span>
            </span>
            <span className="user-chevron" aria-hidden="true">
              {menuOpen ? ICONS.collapse : ICONS.expand}
            </span>
          </button>

          {menuOpen && (
            <div className="user-menu">
              <p className="user-menu-line">
                {visiting ? (
                  <>
                    You are visiting <strong>{me.company_name ?? 'this company'}</strong> for
                    support. You are not one of their people and hold no role here.
                  </>
                ) : (
                  <>
                    Signed in as{' '}
                    <strong>
                      {me.company_role ? roleLabel(me.company_role) : 'Unknown role'}
                    </strong>{' '}
                    at <strong>{me.company_name ?? 'this company'}</strong>.
                  </>
                )}
              </p>
              {/*
                SAID ONLY WHEN TRUE, and scoped to a project when it is.
                `/v1/me` resolves `finance_view` at COMPANY level, and financial
                access lives on a project membership — so `false` here means
                "not established at this level", not "never". Turning that into
                "financial figures are not shown to your role" would be a flat
                lie to a Company Admin who sees money on every project they are
                staffed on.
              */}
              {me.finance_view && (
                <p className="user-menu-line hint">
                  You can see financial figures on the projects you are on.
                </p>
              )}


              <button type="button" className="secondary" onClick={onSignOut} disabled={busy}>
                {visiting
                  ? busy
                    ? 'Leaving…'
                    : 'Leave this company'
                  : busy
                    ? 'Signing out…'
                    : 'Sign out'}
              </button>
            </div>
          )}
        </div>
      </header>

      <main className="page">

        {onHome ? (
          <Portfolio />
        ) : onAlerts ? (
          <Notifications onUnreadChanged={setUnread} />
        ) : onMyWork ? (
          <MyWork />
        ) : onPeople ? (
          <People me={me} />
        ) : onCompany ? (
          <Company me={me} />
        ) : onAudit ? (
          <AuditTrail me={me} />
        ) : projectRoute?.section === ON_SITE ? (
          // A whole screen, not one of the seven sections: somebody using it is
          // recording ONE thing and putting the phone away, not navigating a
          // project. Matched BEFORE `sectionFrom`, which reads an unknown
          // segment as the overview and would swallow it.
          <SiteHome key={projectRoute.id} projectId={projectRoute.id} />
        ) : projectRoute ? (
          // Keyed on the project alone, not the section: moving between
          // sections must not throw away the project the page already read.
          <ProjectDetail
            key={projectRoute.id}
            projectId={projectRoute.id}
            section={sectionFrom(projectRoute.section)}
            /*
              `/projects/<id>/<section>/<register>/<record>` — one record, as its
              own page. Passed DOWN rather than routed to separately, because a
              record page keeps the project head, the stage strip and the tab
              strip: it is a screen inside the project and the reader has not
              left it. `ProjectDetail` is also the one place that has already
              loaded the project's capabilities, which the page's actions need.
            */
            record={
              projectRoute.register && projectRoute.recordId
                ? { register: projectRoute.register, id: projectRoute.recordId }
                : null
            }
            currentUserId={me.user_id}
            visiting={visiting}
          />
        ) : (
          <Projects canCreate={me.can.create_project} />
        )}
      </main>
      </div>
    </>
  );
}
